'use client';

/**
 * CategoryChips — horizontal scrollable chip row for selecting the item type
 * in the Unified Add Sheet.
 *
 * Each chip maps to a provider + category slug.  The selected chip renders
 * with a filled/primary style; unselected chips use an outline style.
 *
 * Exported PROVIDER_CONFIG is consumed by:
 *  - QuickAddSheet  — to derive the correct API provider / category slug
 *  - useUnifiedAdd  — ProviderValue type drives the hook's selectedProvider
 */

import { cn } from '@/lib/utils';
import { getCategoryColor } from '@/components/favely/category-emoji';
export { PROVIDER_CONFIG } from '@/lib/category-config';
export type { ProviderValue } from '@/lib/category-config';
import { PROVIDER_CONFIG } from '@/lib/category-config';
import type { ProviderValue } from '@/lib/category-config';

// ── Component ─────────────────────────────────────────────────────────────────

interface CategoryChipsProps {
  selected: ProviderValue;
  onChange: (value: ProviderValue) => void;
}

export function CategoryChips({ selected, onChange }: CategoryChipsProps) {
  return (
    <div
      className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide"
      role="group"
      aria-label="Item category"
    >
      {PROVIDER_CONFIG.map((p) => {
        const isSelected = selected === p.value;
        const Icon = p.icon;
        const color = getCategoryColor(p.value);

        return (
          <button
            key={p.value}
            type="button"
            onClick={() => onChange(p.value)}
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isSelected
                ? 'border-transparent text-white'
                : 'border-border bg-background text-foreground hover:bg-muted'
            )}
            style={
              isSelected
                ? { backgroundColor: color, borderColor: color }
                : undefined
            }
            aria-pressed={isSelected}
          >
            {/* Lucide outline icon — filled appearance comes from the solid bg */}
            <Icon
              className="h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
              strokeWidth={isSelected ? 2.5 : 1.75}
            />
            {p.label}
          </button>
        );
      })}
    </div>
  );
}
